import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BadgePlus,
  CalendarCheck,
  CircleEllipsis,
  Coins,
  Film,
  Gift,
  Image as ImageIcon,
  Loader2,
  RefreshCcw,
  Sparkles,
  Ticket,
  UserPlus,
  Users,
  UsersRound,
} from 'lucide-react';
import { platformFetch } from '../../platform/http';
import type { NonRechargeCreditCategoryKey, NonRechargeCreditStats } from './types';

function OverviewCard({
  title,
  value,
  recordCount,
  icon: Icon,
  color,
}: {
  title: string;
  value: number;
  recordCount: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-lg border border-light-border">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-light-muted">{title}</p>
          <p className="mt-2 text-2xl font-bold text-light-text">{value.toLocaleString()}</p>
          <p className="mt-1 text-xs text-light-muted">{recordCount.toLocaleString()} 条记录</p>
        </div>
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center shrink-0`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

const CATEGORY_ICON_MAP: Record<NonRechargeCreditCategoryKey, React.ComponentType<{ className?: string }>> = {
  total: Coins,
  checkin: CalendarCheck,
  invitee: UserPlus,
  inviter: UsersRound,
  signup: Gift,
  redeemCode: Ticket,
  imageRefund: ImageIcon,
  videoRefund: Film,
  openaiImageRefund: Sparkles,
  adminGift: BadgePlus,
  other: CircleEllipsis,
};

const CATEGORY_COLOR_MAP: Record<NonRechargeCreditCategoryKey, string> = {
  total: 'bg-amber-500',
  checkin: 'bg-emerald-500',
  invitee: 'bg-lime-500',
  inviter: 'bg-green-600',
  signup: 'bg-teal-500',
  redeemCode: 'bg-cyan-500',
  imageRefund: 'bg-blue-500',
  videoRefund: 'bg-indigo-500',
  openaiImageRefund: 'bg-sky-500',
  adminGift: 'bg-fuchsia-500',
  other: 'bg-slate-500',
};

export default function FreeCreditStatsTab() {
  const [stats, setStats] = useState<NonRechargeCreditStats | null>(null);
  const [selectedSource, setSelectedSource] = useState<NonRechargeCreditCategoryKey>('total');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async (source: NonRechargeCreditCategoryKey) => {
    try {
      setLoading(true);
      setError('');
      const response = await platformFetch(`/admin/free-credit-stats?source=${source}`);
      if (!response.ok) {
        throw new Error('获取非充值积分统计失败');
      }
      const data = (await response.json()) as NonRechargeCreditStats;
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取非充值积分统计失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(selectedSource);
  }, [fetchStats, selectedSource]);

  const categories = useMemo(() => stats?.categories ?? [], [stats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-light-border text-center">
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => fetchStats(selectedSource)}
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-accent-blue px-4 py-2 text-sm font-medium text-white hover:bg-accent-blue/90 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" />
          重试
        </button>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-5 shadow-lg border border-light-border">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-light-muted" />
              <h3 className="font-semibold text-light-text">非充值积分总览</h3>
            </div>
            <p className="mt-2 text-3xl font-bold text-light-text">{stats.totalNonRechargeCredits.toLocaleString()}</p>
            <p className="mt-1 text-sm text-light-muted">共 {stats.totalNonRechargeRecords.toLocaleString()} 条非充值积分记录</p>
          </div>
          <div className="w-full lg:w-auto">
            <label className="block text-sm text-light-muted mb-2">筛选来源 Top 用户</label>
            <select
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value as NonRechargeCreditCategoryKey)}
              className="w-full lg:w-[260px] rounded-xl border border-light-border bg-white px-3 py-2 text-sm text-light-text focus:outline-none focus:ring-2 focus:ring-accent-blue/20"
            >
              {stats.sourceOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {categories.map((category) => {
          const Icon = CATEGORY_ICON_MAP[category.key];
          return (
            <OverviewCard
              key={category.key}
              title={category.label}
              value={category.credits}
              recordCount={category.recordCount}
              icon={Icon}
              color={CATEGORY_COLOR_MAP[category.key]}
            />
          );
        })}
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-lg border border-light-border">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-light-muted" />
            <h3 className="font-semibold text-light-text">{stats.selectedSourceLabel} Top 用户</h3>
          </div>
          <div className="text-sm text-light-muted">
            累计 {stats.selectedSourceCredits.toLocaleString()} 积分 / {stats.selectedSourceRecords.toLocaleString()} 条记录
          </div>
        </div>

        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-light-border">
                <th className="px-2 py-3 text-left text-sm font-medium text-light-muted">排名</th>
                <th className="px-2 py-3 text-left text-sm font-medium text-light-muted">用户</th>
                <th className="px-2 py-3 text-left text-sm font-medium text-light-muted">邮箱</th>
                <th className="px-2 py-3 text-right text-sm font-medium text-light-muted">积分</th>
                <th className="px-2 py-3 text-right text-sm font-medium text-light-muted">记录数</th>
              </tr>
            </thead>
            <tbody>
              {stats.topUsers.map((user, index) => (
                <tr key={user.userId} className="border-b border-light-border last:border-0">
                  <td className="px-2 py-3 text-sm font-medium text-light-text">{index + 1}</td>
                  <td className="px-2 py-3 text-sm text-light-text">{user.name || '未设置昵称'}</td>
                  <td className="px-2 py-3 text-sm text-light-muted">{user.email || '-'}</td>
                  <td className="px-2 py-3 text-sm text-right font-semibold text-light-text">{user.credits.toLocaleString()}</td>
                  <td className="px-2 py-3 text-sm text-right text-light-muted">{user.recordCount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 lg:hidden">
          {stats.topUsers.map((user, index) => (
            <div key={user.userId} className="rounded-xl bg-light-hover p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-light-text">
                    {index + 1}. {user.name || '未设置昵称'}
                  </div>
                  <div className="mt-1 truncate text-xs text-light-muted">{user.email || '-'}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-light-text">{user.credits.toLocaleString()}</div>
                  <div className="text-xs text-light-muted">{user.recordCount.toLocaleString()} 条</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {stats.topUsers.length === 0 && (
          <div className="py-8 text-center text-sm text-light-muted">当前来源暂无用户记录</div>
        )}
      </div>
    </div>
  );
}
